import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaStar, 
  FaSearch, 
  FaFilter, 
  FaUsers, 
  FaClock, 
  FaChild,
  FaTimes,
  FaChevronDown
} from 'react-icons/fa';
import coursesData from '../data/coursesData';

const CoursesPage = () => {
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    level: 'all',
    ageGroup: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Get unique categories, levels, and age groups for filters
  const categories = ['all', ...new Set(coursesData.map(course => course.category))];
  const levels = ['all', ...new Set(coursesData.map(course => course.level))];
  const ageGroups = ['all', ...new Set(coursesData.map(course => course.ageGroup))];
  
  // Filter courses based on search term and filters
  useEffect(() => {
    let results = coursesData;
    
    if (searchTerm) {
      results = results.filter(course => 
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filters.category !== 'all') {
      results = results.filter(course => course.category === filters.category);
    }
    
    if (filters.level !== 'all') {
      results = results.filter(course => course.level === filters.level);
    }
    
    if (filters.ageGroup !== 'all') {
      results = results.filter(course => course.ageGroup === filters.ageGroup);
    }
    
    setFilteredCourses(results);
  }, [searchTerm, filters]);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { 
        duration: 0.4 
      }
    }
  };
  
  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      category: 'all',
      level: 'all',
      ageGroup: 'all'
    });
    setSearchTerm('');
  };

  // Helper function to get course image path
  const getCourseImagePath = (course) => {
    // Map course categories to specific images
    const categoryImages = {
      'Robotics': '/images/programs/course/bot.jpg',
      'Programming': '/images/programs/course/web.jpg',
      'Electronics': '/images/programs/course/pp.png',
      'Science': '/images/programs/course/level1.jpg',
      'Mathematics': '/images/programs/course/dp.png',
      'Game Development': '/images/programs/course/1.png'
    };

    // Get specific image based on course ID if available
    const courseImages = {
      'robotics-beginners': '/images/programs/course/level1.jpg',
      'robotics-advanced': '/images/programs/course/level2.jpg',
      'coding-fundamentals': '/images/programs/course/web.jpg',
      'electronics-workshop': '/images/programs/course/pp.png',
      'science-explorers': '/images/programs/course/13.png',
      'game-development': '/images/programs/course/15.png',
      'math-problem-solving': '/images/programs/course/dp.png'
    };

    // Use specific course image if available, otherwise use category image, or fallback to default
    return courseImages[course.id] || categoryImages[course.category] || '/images/programs/course/bot.jpg';
  };
  
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      select.custom-select {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        background: transparent;
        position: relative;
        z-index: 1;
      }
      select.custom-select:focus {
        outline: none;
        box-shadow: 0 0 0 2px #2563eb33;
      }
      select.custom-select option {
        background: #fff;
        color: #1e293b;
        font-size: 1rem;
        padding: 0.75rem 1.5rem;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <div className="py-16 px-4 bg-gradient-to-r from-[#2046b3] to-[#2d6af6]">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-white animate-float">Our Courses</h1>
          <div className="w-32 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full animate-pulse mx-auto mb-4"></div>
          <p className="text-white text-lg max-w-2xl mx-auto">
            Discover our range of STEM courses designed to inspire curiosity, 
            build technical skills, and prepare students for future success.
          </p>
        </div>
      </div>

      {/* Glass Card for Search/Filters */}
      <div className="container mx-auto px-4 -mt-16">
        <div className="bg-gradient-to-br from-[#e3ecfa]/80 to-[#f5faff]/80 backdrop-blur-lg border border-blue-100 rounded-2xl shadow-2xl p-8 mb-12">
          {/* Search bar */}
          <div className="mb-8">
            <div className="relative w-full max-w-xl mx-auto">
              <input 
                type="text" 
                placeholder="Search courses..." 
                className="w-full pl-12 pr-4 py-3 border-none rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-700 shadow-lg bg-white/80 backdrop-blur-md"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <FaSearch className="text-blue-400" />
              </div>
              {searchTerm && (
                <button 
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-blue-400 hover:text-blue-600"
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          
          {/* Filter toggle button */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <FaFilter className="text-blue-400" /> Filter Options
            </h3>
            <button 
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#2046b3] to-[#2d6af6] text-white font-semibold shadow hover:from-[#2d6af6] hover:to-[#2046b3] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="text-white" />
              {showFilters ? "Hide Filters" : "Show Filters"}
              <FaChevronDown className={`transform transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>
          
          {/* Filter Grid */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Category filter */}
              <div className="filter-group">
                <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2"><FaFilter className="text-blue-400" />Category</label>
                <div className="relative">
                  <select 
                    className="custom-select block w-full pl-4 pr-10 py-3 text-base border-none focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-full shadow bg-white/80 backdrop-blur-md text-gray-700 cursor-pointer"
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                  {/* Custom arrow */}
                  <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-400 text-lg flex items-center">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                  </span>
                </div>
              </div>
              
              {/* Level filter */}
              <div className="filter-group">
                <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2"><FaStar className="text-yellow-400" />Level</label>
                <div className="relative">
                  <select 
                    className="custom-select block w-full pl-4 pr-10 py-3 text-base border-none focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-full shadow bg-white/80 backdrop-blur-md text-gray-700 cursor-pointer"
                    value={filters.level}
                    onChange={(e) => handleFilterChange('level', e.target.value)}
                  >
                    {levels.map(level => (
                      <option key={level} value={level}>
                        {level === 'all' ? 'All Levels' : level}
                      </option>
                    ))}
                  </select>
                  {/* Custom arrow */}
                  <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-400 text-lg flex items-center">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                  </span>
                </div>
              </div>
              
              {/* Age Group filter */}
              <div className="filter-group">
                <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2"><FaChild className="text-blue-400" />Age Group</label>
                <div className="relative">
                  <select 
                    className="custom-select block w-full pl-4 pr-10 py-3 text-base border-none focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-full shadow bg-white/80 backdrop-blur-md text-gray-700 cursor-pointer"
                    value={filters.ageGroup}
                    onChange={(e) => handleFilterChange('ageGroup', e.target.value)}
                  >
                    {ageGroups.map(ageGroup => (
                      <option key={ageGroup} value={ageGroup}>
                        {ageGroup === 'all' ? 'All Age Groups' : ageGroup}
                      </option>
                    ))}
                  </select>
                  {/* Custom arrow */}
                  <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-400 text-lg flex items-center">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Active filters */}
          {(filters.category !== 'all' || filters.level !== 'all' || filters.ageGroup !== 'all' || searchTerm) && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-500">Active filters:</span>
                
                {filters.category !== 'all' && (
                  <span className="bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1.5 rounded-full flex items-center">
                    {filters.category}
                    <button 
                      className="ml-2 text-blue-400 hover:text-blue-600"
                      onClick={() => handleFilterChange('category', 'all')}
                    >
                      <FaTimes size={10} />
                    </button>
                  </span>
                )}
                
                {filters.level !== 'all' && (
                  <span className="bg-green-50 text-green-700 text-xs font-medium px-3 py-1.5 rounded-full flex items-center">
                    {filters.level}
                    <button 
                      className="ml-2 text-green-400 hover:text-green-600"
                      onClick={() => handleFilterChange('level', 'all')}
                    >
                      <FaTimes size={10} />
                    </button>
                  </span>
                )}
                
                {filters.ageGroup !== 'all' && (
                  <span className="bg-purple-50 text-purple-700 text-xs font-medium px-3 py-1.5 rounded-full flex items-center">
                    {filters.ageGroup}
                    <button 
                      className="ml-2 text-purple-400 hover:text-purple-600"
                      onClick={() => handleFilterChange('ageGroup', 'all')}
                    >
                      <FaTimes size={10} />
                    </button>
                  </span>
                )}
                
                {searchTerm && (
                  <span className="bg-yellow-50 text-yellow-700 text-xs font-medium px-3 py-1.5 rounded-full flex items-center">
                    "{searchTerm}"
                    <button 
                      className="ml-2 text-yellow-400 hover:text-yellow-600"
                      onClick={() => setSearchTerm('')}
                    >
                      <FaTimes size={10} />
                    </button>
                  </span>
                )}
                
                <button 
                  className="ml-auto text-sm text-red-500 hover:text-red-700 font-medium"
                  onClick={clearFilters}
                >
                  Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Results count */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800">
            {filteredCourses.length} {filteredCourses.length === 1 ? 'Course' : 'Courses'} Found
          </h2>
        </div>
        
        {/* Course Grid */}
        {filteredCourses.length > 0 ? (
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {filteredCourses.map(course => (
              <motion.div 
                key={course.id}
                className="bg-white/80 backdrop-blur-lg border border-blue-100 rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group min-h-[480px] flex flex-col"
                variants={itemVariants}
              >
                <div className="relative h-48 bg-gradient-to-r from-[#2046b3] to-[#2d6af6]">
                  <img 
                    src={getCourseImagePath(course)} 
                    alt={course.title}
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105 opacity-90"
                  />
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-3 py-1 m-2 rounded-full shadow">
                    {course.level}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1 justify-between">
                  <div className="flex items-center mb-2">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {course.category}
                    </span>
                    <div className="flex items-center ml-auto">
                      <FaStar className="text-yellow-400" />
                      <span className="text-sm text-gray-700 ml-1">{course.rating}</span>
                    </div>
                  </div>
                  <h3 className="text-lg md:text-xl font-extrabold text-blue-900 mb-2 tracking-tight group-hover:text-blue-700 transition-colors">{course.title}</h3>
                  <p className="text-gray-700 text-base mb-4 line-clamp-2">{course.shortDescription}</p>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="flex items-center text-gray-600 text-sm">
                      <FaClock className="mr-1" /> {course.duration}
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <FaChild className="mr-1" /> {course.ageGroup}
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <FaUsers className="mr-1" /> {course.studentsEnrolled}+ enrolled
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <span className={`w-2 h-2 rounded-full mr-1 ${
                        course.availability === 'Available' 
                          ? 'bg-green-500' 
                          : 'bg-yellow-500'
                      }`}></span> 
                      {course.availability}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4">
                    <span className="text-xl font-bold text-blue-600">{course.price}</span>
                    <Link 
                      to={`/courses/${course.id}`}
                      className="px-5 py-2 rounded-full font-semibold bg-gradient-to-r from-[#2046b3] to-[#2d6af6] text-white shadow hover:from-[#2d6af6] hover:to-[#2046b3] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      tabIndex={0}
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No courses found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filters to find what you're looking for.</p>
            <button 
              className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
              onClick={clearFilters}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage; 